import { SupabaseClient } from '@supabase/supabase-js';
import type { Database, Json } from '@/integrations/supabase/types';
import { EventData, ParticipantDetails } from '@/contexts/event/EventContext';

export class SupabaseEventsService {
  constructor(private supabase: SupabaseClient<Database>) {}

  private async logEventAction(
    userId: string,
    action: string,
    eventId: string,
    metadata: Record<string, unknown> = {}
  ) {
    if (!userId) return;
    try {
      const payload: Database['public']['Functions']['log_player_action']['Args'] = {
        _action: action,
        _entity_type: 'event',
        _entity_id: eventId,
        _metadata: metadata as Json,
        _context: 'events-service',
        _user_id: userId,
      };
      await this.supabase.rpc('log_player_action', payload);
    } catch (error) {
      console.warn('logEventAction failed', error);
    }
  }

  async getEvents(): Promise<EventData[]> {
    const evRes = await this.supabase
      .from('events')
      .select('*')
      .order('event_date', { ascending: true });
    if (evRes.error || !evRes.data) {
      console.error('Error fetching events:', evRes.error);
      return [];
    }
    const events = evRes.data as any[];
    const eventIds = events.map(e => e.id);
    const groupIds = Array.from(new Set(events.map(e => e.group_id).filter(Boolean)));

    const [partsRes, waitsRes, groupsRes] = await Promise.all([
      this.supabase.from('event_participants').select('event_id,user_id,joined_at').in('event_id', eventIds),
      this.supabase.from('event_waitlist').select('event_id,user_id,position,joined_at').in('event_id', eventIds),
      groupIds.length ? this.supabase.from('groups').select('id,name,color').in('id', groupIds) : Promise.resolve({ data: [] } as any),
    ]);

    const parts = partsRes.data || [];
    const waits = waitsRes.data || [];
    const groupNameMap = new Map<string, string>();
    const groupColorMap = new Map<string, string | null>();
    (groupsRes.data || []).forEach((g: any) => {
      groupNameMap.set(g.id, g.name);
      groupColorMap.set(g.id, g.color);
    });

    const userIds = Array.from(new Set([...parts, ...waits].map((r: any) => r.user_id.toString())));
    const profileMap = new Map<string, { first_name?: string; last_name?: string; phone?: string; email?: string }>();
    if (userIds.length) {
      const pRes = await this.supabase.from('profiles_public_v').select('user_id,first_name,last_name,phone,email').in('user_id', userIds);
      if (pRes.error) {
        console.error('[SupabaseEventsService] Error fetching profiles:', pRes.error);
      }
      console.log(`[SupabaseEventsService] Fetched ${(pRes.data || []).length} profiles for ${userIds.length} user IDs`);
      (pRes.data || []).forEach((p: any) => profileMap.set(p.user_id, { first_name: p.first_name, last_name: p.last_name, phone: p.phone, email: p.email }));
    }

    // Load group memberships for users
    const groupsByUser = new Map<string, string[]>();
    if (userIds.length) {
      const gmRes = await this.supabase
        .from('group_members')
        .select('user_id, groups:groups!inner(name)')
        .in('user_id', userIds);
      (gmRes.data || []).forEach((row: any) => {
        const name = row.groups?.name as string | undefined;
        if (!name) return;
        const list = groupsByUser.get(row.user_id) || [];
        list.push(name);
        groupsByUser.set(row.user_id, list);
      });
    }

    return events.map(event => {
      const evParts = parts.filter((p: any) => p.event_id === event.id);
      const evWaits = waits.filter((w: any) => w.event_id === event.id);
      const participantDetails: ParticipantDetails[] = evParts.map((p: any) => {
        const userId = p.user_id.toString();
        const prof = profileMap.get(userId) || {} as any;
        const fn = prof.first_name || '';
        const ln = prof.last_name || '';
        const name = (fn || ln) ? `${fn} ${ln}`.trim() : 'Unknown';
        return { 
          id: p.user_id, 
          name, 
          phone: prof.phone || '', 
          email: prof.email || '',
          isAdmin: false, 
          groups: groupsByUser.get(p.user_id) || [],
          joinedAt: p.joined_at ? new Date(p.joined_at) : undefined
        };
      });
      const waitingListDetails: ParticipantDetails[] = evWaits.map((w: any) => {
        const userId = w.user_id.toString();
        const prof = profileMap.get(userId) || {} as any;
        const fn = prof.first_name || '';
        const ln = prof.last_name || '';
        const name = (fn || ln) ? `${fn} ${ln}`.trim() : 'Unknown';
        return { 
          id: w.user_id, 
          name, 
          phone: prof.phone || '', 
          email: prof.email || '',
          isAdmin: false, 
          groups: groupsByUser.get(w.user_id) || [],
          joinedAt: w.joined_at ? new Date(w.joined_at) : undefined
        };
      });

      return {
        id: event.id,
        title: event.title,
        description: event.description || '',
        titleKey: event.title_key,
        descriptionKey: event.description_key,
        date: new Date(event.event_date + 'T' + event.event_time),
        time: event.event_time,
        location: event.location,
        group: groupNameMap.get(event.group_id) || event.group_key || '',
        groupKey: event.group_key,
        groupColor: groupColorMap.get(event.group_id) || undefined,
        level: event.level,
        participants: evParts.map((p: any) => p.user_id),
        participantDetails,
        maxParticipants: event.max_participants,
        type: event.event_type,
        notes: event.notes,
        waitingList: evWaits.map((w: any) => w.user_id),
        waitingListDetails,
        customSignupHours: event.custom_signup_hours,
        recurrencePattern: event.recurrence_pattern,
        recurrenceEndDate: event.recurrence_end_date ? new Date(event.recurrence_end_date) : undefined,
        parentEventId: event.parent_event_id,
        isRecurringMaster: event.is_recurring_master,
      } as EventData;
    });
  }

  async signUpForEvent(eventId: string, userId: string): Promise<boolean> {
    console.log('[SupabaseEventsService] signUpForEvent called with userId:', userId, 'type:', typeof userId);
    
    // Verify the user exists in profiles table before attempting signup
    const { data: profileCheck, error: profileError } = await this.supabase
      .from('profiles')
      .select('user_id, first_name, last_name')
      .eq('user_id', userId)
      .maybeSingle();
    
    console.log('[SupabaseEventsService] Profile check before signup:', {
      userId,
      profileExists: !!profileCheck,
      profileError: profileError?.message,
      profile: profileCheck,
    });
    
    if (!profileCheck) {
      console.error('[SupabaseEventsService] CRITICAL: User profile does not exist in profiles table!', {
        userId,
        error: profileError,
      });
      // Don't return false yet - let's see if the insert provides more details
    }
    
    const { data, error } = await this.supabase
      .from('event_participants')
      .insert({ event_id: eventId, user_id: userId })
      .select();

    if (error) {
      console.error('[SupabaseEventsService] Error signing up for event:', {
        error,
        code: error.code,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
      return false;
    }

    if (!data || data.length === 0) {
      console.error('Sign up failed: No data returned after insert.');
      return false;
    }

    console.log('[SupabaseEventsService] Successfully signed up for event:', data);
    await this.logEventAction(userId, 'event.signup', eventId);
    return true;
  }

  async cancelEventSignup(eventId: string, userId: string): Promise<boolean> {
    const { error } = await this.supabase
      .from('event_participants')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error canceling event signup:', error);
      return false;
    }
    await this.logEventAction(userId, 'event.cancel', eventId);
    return true;
  }

  async joinWaitingList(eventId: string, userId: string): Promise<number | null> {
    // Get current max position
    const { data: waitlist } = await this.supabase
      .from('event_waitlist')
      .select('position')
      .eq('event_id', eventId)
      .order('position', { ascending: false })
      .limit(1);

    const nextPosition = waitlist && waitlist.length > 0 ? (waitlist[0].position || 0) + 1 : 1;

    const { data, error } = await this.supabase
      .from('event_waitlist')
      .insert({ 
        event_id: eventId, 
        user_id: userId,
        position: nextPosition
      })
      .select();

    if (error) {
      console.error('Error joining waiting list:', error);
      return null;
    }

    if (!data || data.length === 0) {
      console.error('Joining waiting list failed: No data returned after insert.');
      return null;
    }

    await this.logEventAction(userId, 'event.waitlist.join', eventId, { position: nextPosition });
    return nextPosition;
  }

  async leaveWaitingList(eventId: string, userId: string): Promise<boolean> {
    const { error } = await this.supabase
      .from('event_waitlist')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error leaving waiting list:', error);
      return false;
    }
    await this.logEventAction(userId, 'event.waitlist.leave', eventId);
    return true;
  }

  async removeParticipant(eventId: string, participantId: string): Promise<boolean> {
    // Try delete with returning to detect RLS or zero rows
    const res = await this.supabase
      .from('event_participants')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', participantId)
      .select('id');
    if ((res.error && res.status !== 204) || (res.data && res.data.length > 0)) {
      if (!res.error) {
        await this.logEventAction(participantId, 'event.removed', eventId);
        return true;
      }
      return false;
    }

    // Fallback to edge function with service role to perform admin removal + promotion
    try {
      const { data, error } = await this.supabase.functions.invoke('manage-event', {
        body: { action: 'remove_participant', event_id: eventId, user_id: participantId },
      });
      if (error) throw error;
      if (data?.success) {
        await this.logEventAction(participantId, 'event.removed', eventId, { via: 'edge' });
      }
      return !!data?.success;
    } catch (e) {
      console.error('Edge remove_participant failed:', e);
      return false;
    }
  }
}
